import { DataItem } from '../../../@types/DataItem';
import { api } from '../../../lib';
import {
  ApplyNetworkPriceItem,
  EditedNetworkClusterPrice,
  NetworkCategoryKey,
  NetworkFilterValues,
  NetworkOptionsCascade,
  NetworkOptionsLookup,
  NetworkPricesPage,
  NetworkPricesPagination,
} from './types';

// The body takes only the active filters; a line reload sends only productFamilyIds or productIds.
export const getNetworkPrices = async (pagination: NetworkPricesPagination, filter?: NetworkFilterValues): Promise<NetworkPricesPage> => {
  const { data } = await api.post<NetworkPricesPage>('/all-products/global', filter ?? {}, {
    expectToken: true,
    params: { ...pagination, sort: 'product_description,asc' },
  });

  return data;
};

export const editClusterPrice = async (productsToBePricedId: string, editedPrice: number): Promise<void> => {
  await api.put('/all-products/global/price', { productsToBePricedId, editedPrice }, { expectToken: true });
};

// Atomic: every cluster of the line is saved or none is.
export const editNetworkAveragePrice = async (productsToBePricedIds: string[], editedPrice: number): Promise<EditedNetworkClusterPrice[]> => {
  const { data } = await api.put<EditedNetworkClusterPrice[]>(
    '/all-products/global/average-price',
    { productsToBePricedIds, editedPrice },
    { expectToken: true },
  );

  return data;
};

export const applyNetworkPrices = async (products: ApplyNetworkPriceItem[]): Promise<void> => {
  await api.post('/all-products/global/apply', { products }, { expectToken: true });
};

type ProductOptionResponse = { product_id: string | number; description: string };

// /products/search wraps the list in its own `data` field.
type ProductOptionsResponse = { data: ProductOptionResponse[] };

type FamilyOptionsResponse = { product_family_ids: string[] };

type BrandOptionResponse = { productBrand: string };

type SupplierOptionResponse = { supplier: string };

export const NETWORK_FILTER_OPTIONS_SIZE = 20;
export const NETWORK_FILTER_PASTE_SIZE = 1000;

const getClientId = (): number | undefined => JSON.parse(localStorage.getItem('cliente') ?? 'null')?.codigo;

const getSize = ({ isPaste = false }: NetworkOptionsLookup): number => (isPaste ? NETWORK_FILTER_PASTE_SIZE : NETWORK_FILTER_OPTIONS_SIZE);

const toOption = (value: string): DataItem => ({ label: value, value });

export const listNetworkFieldOptions = async (field: NetworkCategoryKey, query: string, cascade: NetworkOptionsCascade): Promise<DataItem[]> => {
  const { data } = await api.post<string[]>(
    '/products/filters',
    { field, ...(query && { query }), ...cascade },
    { expectToken: true, params: { clientId: getClientId(), size: NETWORK_FILTER_OPTIONS_SIZE } },
  );

  return data.map(toOption);
};

export const listNetworkProducts = async (
  query: string,
  { productFamilyId, ...cascade }: NetworkOptionsCascade,
  options: NetworkOptionsLookup = {},
): Promise<DataItem[]> => {
  const { data } = await api.post<ProductOptionsResponse>(
    '/products/search',
    { ...cascade, ...(productFamilyId && { productFamilyIds: productFamilyId }) },
    { expectToken: true, params: { clientId: getClientId(), query, size: getSize(options), isModal: !!options.isPaste } },
  );

  return data.data.map(({ product_id, description }) => ({ label: `${product_id} - ${description}`, value: String(product_id), description }));
};

export const listNetworkFamilies = async (query: string, cascade: NetworkOptionsCascade, options: NetworkOptionsLookup = {}): Promise<DataItem[]> => {
  const { data } = await api.post<FamilyOptionsResponse>(
    '/products/families/id',
    { ...(query && { query }), ...cascade },
    { expectToken: true, params: { size: getSize(options) } },
  );

  return data.product_family_ids.map(toOption);
};

// Same endpoints as the price manager brand and supplier filters, which accept only the search.
export const listNetworkBrands = async (query: string): Promise<DataItem[]> => {
  const { data } = await api.get<BrandOptionResponse[]>('/products/productBrands', { expectToken: true, params: { ...(query && { search: query }) } });

  return data.map(({ productBrand }) => toOption(productBrand));
};

export const listNetworkSuppliers = async (query: string): Promise<DataItem[]> => {
  const { data } = await api.get<SupplierOptionResponse[]>('/products/suppliers', { expectToken: true, params: { ...(query && { search: query }) } });

  return data.map(({ supplier }) => toOption(supplier));
};
